import { useMemo, useState } from 'react';

import { type ClientSector, type ClientSectorGroup, clientSectorGroups } from '@/content/clients';

/** `null` is "all sectors", which is the state the page is prerendered in. */
export type SectorFilter = ClientSector | null;

/**
 * The roster's filter state and the groups it resolves to.
 *
 * The filter narrows which *groups* are drawn rather than flattening the
 * roster into a filtered list, which is what keeps one code path in the band:
 * unfiltered is fifteen groups, filtered is one, and the grid underneath
 * never learns which it is.
 *
 * Default is unfiltered on purpose, and not only as a kindness to the reader.
 * The site is prerendered to static HTML (ADR 0001), so whatever this returns
 * on the first render is what a crawler and a link preview get — all
 * every name, in readable text, with their sector headings. A page that
 * defaulted to a selected sector would publish a sixth of its own content.
 *
 * No URL state. A sector is a glance, not a destination: nobody links a
 * colleague to "the banking slice of the client list", and a query parameter
 * would add a second source of truth, a second prerender question and a
 * history entry per chip tap. If deep links are ever wanted, this hook is the
 * one place that changes.
 */
export function useClientRoster() {
  const [sector, setSector] = useState<SectorFilter>(null);

  const groups = useMemo<readonly ClientSectorGroup[]>(
    () =>
      sector ? clientSectorGroups.filter((group) => group.sector === sector) : clientSectorGroups,
    [sector],
  );

  const shown = useMemo(
    () => groups.reduce((total, group) => total + group.clients.length, 0),
    [groups],
  );

  return {
    sector,
    /** Tapping the chip that is already on clears it, as the delivery filters do. */
    select: (next: ClientSector) => setSector((current) => (current === next ? null : next)),
    clear: () => setSector(null),
    groups,
    shown,
  };
}

/**
 * The filter, as the two bands that now read it see it.
 *
 * It was internal to `ClientRosterBand` while the band was the only thing that
 * cared. The sector call to action at the foot of the roster made the filter a
 * page-level fact — what a reader filtered to decides what the ask says and
 * which sector the form is prefilled with — so the hook is called once by
 * `ClientsPage` and handed down. One source of truth, and no context for two
 * consumers.
 */
export type ClientRosterState = ReturnType<typeof useClientRoster>;
